import React, { useEffect, useState } from 'react';
import { LineItemsSection } from '@/styles/LineItemsSection';
import { StandardButton } from '@/styles/StandardButtons';
import { emptyLine, withDefaultCostCode, type LineItem } from '@/lib/line-items';
import { costCodeLabel, defaultCostCodeOf, operationalCodes, useCostCodes } from '@/lib/cost-codes';
import { fetchOffersByItem, searchCatalog, type CatalogSearchOffer, type CatalogSearchRow } from '../../catalog/services/catalog-api';
import { lineFromCatalog, lineVendorOptions, suggestionOf, switchLineVendor, unlinkIfModified, usesPreferredOffer, withManualVendor } from '../lib/po-lines';
import { AddFromCatalogDialog } from './AddFromCatalogDialog';

interface Vendor { id: string; name: string }

/**
 * The state and handlers every document made of catalog-style items needs (a purchase order, a change order): the vendor offers of the
 * items on it, the company's cost codes and default, and what picking / adding / editing a line does. The document keeps its own `lines`.
 * `vendorId` is the document's vendor (a purchase order's; a document with none passes null): new lines are priced with it.
 */
export function useCatalogLines({ isOpen, lines, setLines, vendorId, vendors }: { isOpen: boolean; lines: LineItem[]; setLines: React.Dispatch<React.SetStateAction<LineItem[]>>; vendorId: string | null; vendors: Vendor[] }) {
  // the vendor offers of the catalog items on the document: what each line's Vendor dropdown lists, read once per item
  const [offersByItem, setOffersByItem] = useState<Map<string, CatalogSearchOffer[]>>(new Map());
  const { codes: companyCostCodes } = useCostCodes(isOpen);
  // The company's default cost code, from its real records (lib/cost-codes.ts): every new line starts with it. A company without one gets '' -- no id is
  // invented, and a line without a code cannot be saved.
  const defaultCostCodeId = defaultCostCodeOf(companyCostCodes)?.id ?? '';
  const rememberOffersOf = (itemIds: string[]) => {
    const ids = Array.from(new Set(itemIds));
    if (ids.length === 0) return;
    fetchOffersByItem(ids)
      .then((found) => setOffersByItem((previous) => { const next = new Map(previous); ids.forEach((id) => next.set(id, (found.get(id) ?? []) as unknown as CatalogSearchOffer[])); return next; }))
      .catch((failure) => console.error('Error loading the vendor offers of the items:', failure));
  };
  // a line added before the company's cost codes finished loading gets the default as soon as it is known (saved lines are never touched)
  useEffect(() => {
    setLines((previous) => withDefaultCostCode(previous, defaultCostCodeId));
  }, [defaultCostCodeId, setLines]);

  const vendorNameOf = (id: string) => vendors.find((v) => v.id === id)?.name ?? 'Vendor';
  // The codes a line can be given: the company's active ones, plus any code a line already has (e.g. one deactivated since): never "none"
  const costCodeOptions = operationalCodes(companyCostCodes, lines.map((line) => line.costCodeId)).map((c) => ({ value: c.id, label: costCodeLabel(c) }));

  // Picking an item for a line: the line becomes that item (linked, with the item's cost code -- else the company's default -- and the
  // vendor's price). The same item may be on the document as many times as wanted: every line is its own.
  const pickCatalogItem = (lineKey: string, item: CatalogSearchRow) => {
    setOffersByItem((previous) => new Map(previous).set(item.id, item.offers));
    setLines((previous) => previous.map((line) => (line.key === lineKey ? { ...lineFromCatalog(item, vendorId, line.quantity, defaultCostCodeId), key: line.key } : line)));
  };

  // A single line names another vendor. For a line from the catalog it is one of the item's offers (and brings that offer's price); for a custom
  // line it is just a vendor of the company -- the typed price stays. The document's own vendor is not touched.
  const handleLineVendorChange = (lineKey: string, nextVendorId: string) => {
    const line = lines.find((l) => l.key === lineKey);
    if (!line) return;
    if (!line.catalogItemId) {
      setLines((previous) => previous.map((l) => (l.key === lineKey ? withManualVendor(l, nextVendorId || null) : l)));
      return;
    }
    if (!nextVendorId) return;
    const switched = switchLineVendor(line, lineVendorOptions(line, offersByItem), nextVendorId);
    if (!switched) return;
    if (switched.replacesManualPrice && !window.confirm(`You edited the price of ${line.name || 'this item'}. Switching the vendor replaces it with the vendor's price. Continue?`)) return;
    setLines((previous) => previous.map((l) => (l.key === lineKey ? switched.line : l)));
  };

  const addCatalogItems = (items: CatalogSearchRow[]) => {
    setOffersByItem((previous) => { const next = new Map(previous); items.forEach((item) => next.set(item.id, item.offers)); return next; });
    setLines((previous) => [...previous, ...items.map((item) => lineFromCatalog(item, vendorId, '1', defaultCostCodeId))]);
  };

  const addEmptyLine = () => setLines((previous) => [...previous, emptyLine(defaultCostCodeId)]);

  // Every edit goes through here: a catalog line whose name, cost code or unit price no longer matches its item becomes a custom line
  const changeLines = (next: LineItem[]) => setLines(next.map(unlinkIfModified));

  return { offersByItem, setOffersByItem, rememberOffersOf, defaultCostCodeId, costCodeOptions, vendorNameOf, pickCatalogItem, handleLineVendorChange, addCatalogItems, changeLines, addEmptyLine };
}

/**
 * THE items section of a purchase order (and of every document that takes the same items): the generic item table with the catalog search, the
 * vendor dropdowns, "Add Line" and "Add from Catalog". `catalog` is the result of `useCatalogLines` for the same lines.
 */
export function CatalogLineItems({ catalog, lines, vendorId, vendors, loading, loadError, disabled, adjustments, totalLabel, onLinesChange }: {
  catalog: ReturnType<typeof useCatalogLines>;
  lines: LineItem[];
  vendorId: string | null;
  vendors: Vendor[];
  loading?: boolean;
  loadError?: string | null;
  /** true while the lines are not ready to be edited (an existing document's items are still being read) */
  disabled?: boolean;
  adjustments?: React.ComponentProps<typeof LineItemsSection>['adjustments'];
  /** the label of the single Total row when the document has no `adjustments` (a document that adds its own charges below says "Subtotal:") */
  totalLabel?: string;
  /** the document's own handler when an edit needs more than replacing the lines (default: replace them) */
  onLinesChange?: (next: LineItem[]) => void;
}) {
  const [catalogOpen, setCatalogOpen] = useState(false);
  const { offersByItem, costCodeOptions, vendorNameOf, pickCatalogItem, handleLineVendorChange, addCatalogItems, changeLines, addEmptyLine } = catalog;
  return (
    <>
      <LineItemsSection<CatalogSearchRow>
        lines={lines}
        onChange={onLinesChange ?? changeLines}
        searchItems={async (term) => (await searchCatalog({ search: term, limit: 8 })).rows.map((row) => suggestionOf(row, vendorId))}
        onPickItem={(lineKey, suggestion) => pickCatalogItem(lineKey, suggestion.data)}
        costCodeOptions={costCodeOptions}
        // a line from the catalog: the vendors that have an offer for its item; a custom line: any vendor of the company
        vendorOptions={(line) => (line.catalogItemId
          ? lineVendorOptions(line, offersByItem).map((offer) => ({ value: offer.vendor_id, label: vendorNameOf(offer.vendor_id) }))
          : vendors.map((v) => ({ value: v.id, label: v.name })))}
        vendorName={vendorNameOf}
        onVendorChange={handleLineVendorChange}
        isPreferredPrice={(line) => usesPreferredOffer(line, offersByItem)}
        adjustments={adjustments}
        totalLabel={totalLabel}
        loading={loading}
        loadError={loadError}
        disabled={disabled}
        toolbar={(
          <>
            <StandardButton type="button" customVariant="white" disabled={disabled} onClick={() => addEmptyLine()}>Add Line</StandardButton>
            <StandardButton type="button" customVariant="white" disabled={disabled} onClick={() => setCatalogOpen(true)}>Add from Catalog</StandardButton>
          </>
        )}
      />
      <AddFromCatalogDialog isOpen={catalogOpen} onClose={() => setCatalogOpen(false)} vendors={vendors} onAdd={addCatalogItems} />
    </>
  );
}
